import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function DerbyCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Derby Pothole Claims | A-Road Green Share Halved | Fixtyer"
        description="Derby City Council's A-road 'green' condition fell from 79% to 30% in two years, while pothole repairs rose to 3,844 in 2024/25. See what the DfT AMBER rating means for your claim — distinct from Derbyshire County Council."
        canonical="/councils/derby"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Derby', url: '/councils/derby' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Is Derby City Council the same as Derbyshire County Council for pothole claims?',
            answer: 'No. Derby is a unitary city council maintaining 765.45km of carriageway within the city boundary. Derbyshire County Council maintains roads outside the city. If your incident was on a road inside Derby — for example London Road, Uttoxeter New Road or a residential street in Alvaston or Mickleover — your claim is against Derby City Council, not the county.',
          },
          {
            question: 'Derby earns a GREEN best-practice scorecard — can I still claim?',
            answer: 'Yes. Best practice reflects asset-management processes, not whether the specific pothole that damaged your vehicle was reasonably inspected and repaired. Derby\'s own Annual Engineering Inspection data shows A-road "green" share fell from 79% in 2022 to 30% in 2024, B and C roads remain at 17% RED, and the council filled an estimated 3,844 potholes in 2024/25. Section 58 turns on the defect, not the scorecard colour.',
          },
          {
            question: 'What if my pothole was on a residential street in Derby?',
            answer: 'Unclassified roads make up 610.94km — roughly 80% of Derby City Council\'s 765.45km network. The council surveys only 25% to 50% of U-roads each year under DfT reporting standards, meaning large parts of the residential network may not have fresh condition data in any given year. At the last published survey, 7% of U-roads were in RED condition (down from a steady 10% in 2021–2023).',
          },
          {
            question: 'Derby says rising pothole repairs mean roads are improving — does that weaken my claim?',
            answer: 'Not necessarily — but it is an argument the council may deploy. Derby attributes higher repair counts partly to £1.2 million of DfT Pothole Fund patching and fixing multiple defects in one visit. That does not prove the specific defect that damaged your vehicle was caught in time. Prior reports, photos showing defect age, and the council\'s own condition tables remain the decisive evidence.',
          },
          {
            question: 'Does Derby\'s switch to AEI surveys help or hurt my claim?',
            answer: 'It cuts both ways. Derby moved to Annual Engineering Inspection in 2018 and states openly that AEI "may show more of the road network in poor condition than SCANNER" because it assesses entire maintenance sections. That is an admission the previous national benchmark understated local need. If your incident post-dates AEI reporting, the council chose a methodology that shows 61% of A-roads in amber condition — and cannot revert to SCANNER to downplay it.',
          },
          {
            question: 'Derby\'s capital spend now matches its DfT allocation — what does AMBER spend mean?',
            answer: 'From 2022/23 onwards Derby\'s capital spend has matched its DfT allocation year-on-year (unlike the £9 million cabinet boost split across 2020/21 and 2021/22). The AMBER spend scorecard reflects that recent alignment, not a funding crisis. For your claim, condition data matters more: 67% of B and C roads are in RED or amber categories, and reactive pothole filling still runs at roughly 3,800 repairs a year.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Derby City Council"
        councilSlug="derby"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/derby"
        headline="Derby City Council Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Derby</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Derby: A-road &lsquo;green&rsquo; share collapsed from 79% to 30%
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Derby City Council — the unitary authority for the city, distinct from Derbyshire County Council —
            earns a <strong className="text-emerald-400">GREEN</strong> best-practice scorecard and directs
            <strong className="text-emerald-400"> 80% of maintenance spend</strong> to preventative work.
            Yet the overall DfT rating is <strong className="text-amber-400">AMBER</strong> because on the
            council&apos;s own Annual Engineering Inspection, A-roads in good condition fell from
            <strong className="text-rose-400"> 79% to 30% in two years</strong>, amber share on main roads
            hit 61%, and the council filled an estimated
            <strong className="text-rose-400"> 3,844 potholes in 2024/25</strong> on a 765km network where
            611km is residential streets.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">61%</div>
            <div className="text-white font-medium mt-2">A-roads in amber condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 15% in 2022 while &ldquo;green&rdquo; A-roads fell from 79% to 30% — on just 73.38km
              of classified main roads surveyed annually under Derby&apos;s own AEI methodology.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of AEI survey data from Derby City Council&apos;s own transparency report —
            main roads deteriorating, local roads stable but largely unsurveyed
          </p>

          {/* A-roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">A-roads (73.38km — 9.6% of network): sharp reversal</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">14%</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">N/A</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">N/A</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">12%</td>
                    <td className="px-4 py-2 text-center">51%</td>
                    <td className="px-4 py-2 text-center">37%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-emerald-50">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-700">6%</td>
                    <td className="px-4 py-2 text-center">15%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-700">79%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">8%</td>
                    <td className="px-4 py-2 text-center">58%</td>
                    <td className="px-4 py-2 text-center">34%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">9%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">61%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">30%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Derby&apos;s best A-road year on record was 2022 — 79% green, just 6% red. Two years later,
              fewer than one-third of A-road kilometres are in the green category and nearly two-thirds need
              maintenance soon or now. RED share has also climbed back from its 6% low to 9%.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (81.13km — 10.6% of network): stuck in amber</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">17%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">flat since 2020 (17%)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">50%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">up from 25% in 2022</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">33%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">down from 62% in 2022</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              B and C roads are fully surveyed every year. Two-thirds of this 81km classified local network
              is in RED or amber condition — maintenance may be required now or soon on roughly 54km of
              connector roads across the city.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">80% preventative — yet AMBER spend</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-amber-400">£4.89m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">£4.89m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">80%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share (2024/25)</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Derby now spends its DfT allocation pound-for-pound — after the £9 million cabinet boost that
              doubled capital investment in 2020/21 and 2021/22. The AMBER spend scorecard reflects recent
              alignment, not austerity. But{' '}
              <span className="text-amber-400">condition on main roads is still slipping</span> despite
              15.175km of preventative carriageway work in 2024/25 alone.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road blind spot */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 611km residential network
          </h2>
          <p className="text-surface-600 mb-8">
            80% of Derby&apos;s carriageways are unclassified — surveyed at only 25% to 50% each year
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">8%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">7%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The partial-survey gap</h3>
              <p className="text-surface-600 text-sm mb-3">
                Derby City Council undertakes annual AEI surveys of between 25% and 50% of its U-roads,
                &ldquo;in line with DfT reporting standards.&rdquo; On a 610.94km residential network, that
                means <strong>153km to 305km may go uncondition-surveyed in any given year</strong> — even
                though safety inspections continue on a separate schedule.
              </p>
              <p className="text-surface-600 text-sm">
                At 10% RED (2021–2023), roughly 61km of residential streets were in the worst category at
                each comparable survey. The 2024 figure of 7% RED is an improvement on paper — but it covers
                only the fraction of U-roads surveyed that year.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">City boundary, county confusion</h3>
              <p className="text-amber-800 text-sm mb-3">
                Derby is a unitary authority — it maintains all 765.45km of city roads itself. Surrounding
                areas such as Spondon fringe routes, Mickleover approaches and A38 corridors may involve
                careful boundary checks: some stretches are city, others Derbyshire County Council or
                National Highways.
              </p>
              <p className="text-amber-800 text-sm">
                Filing against the wrong authority delays your claim. The council&apos;s transparency report
                covers <strong>Derby city only</strong> — not Chesterfield, Belper or other Derbyshire towns.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, Derby must show it had a reasonable system for knowing road
              condition. On the city&apos;s residential network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your street in the 25% or 50% of U-roads condition-surveyed the year of your incident?</li>
              <li>• If 10% of U-roads were RED at the last full-comparable survey, what was done about yours?</li>
              <li>• Does a safety walk-over inspection satisfy the same standard as network condition data?</li>
              <li>• Can the council prove it knew about deterioration between partial AEI survey cycles?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim comprehensive network knowledge when it condition-surveys at most half
              of its residential roads each year.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repair trend */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            3,844 pothole repairs in 2024/25
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated reactive repairs rising year-on-year — the council&apos;s own five-year trend
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Estimated potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">2,797</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">2,797</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">3,072</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">3,408</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">3,844</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">38% more repairs than 2020/21</h3>
              <p className="text-surface-600 text-sm">
                Derby&apos;s own report puts the five-year average at approximately 3,280 pothole repairs
                per year. For 2025/26 it projects 4,193 fills — continuing the upward trend. A network
                producing defects at that rate is one where potholes routinely form between inspections,
                regardless of how the council frames the statistics.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The council&apos;s counter-narrative</h3>
              <p className="text-amber-800 text-sm">
                Derby attributes higher repair counts to £1.2 million of DfT Pothole Fund patching and
                fixing multiple defects in one visit. It states this &ldquo;doesn&apos;t mean the condition
                of our roads is getting worse — in fact, it shows we&apos;re getting more done.&rdquo; That
                is their interpretation. Your claim still turns on whether the specific defect was known and
                repaired in reasonable time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AEI honesty admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The AEI honesty admission
          </h2>
          <p className="text-surface-600 mb-8">
            Derby switched to Annual Engineering Inspection in 2018 — and publishes what it means
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Although the AEI may show more of the road network in poor condition than SCANNER, it is because it takes a more complete, engineering-based view of entire maintenance sections. This gives a genuine picture of what work is actually needed and matches how maintenance is delivered in practice."
              source="Derby City Council Local Highway Maintenance Transparency Report (2025)"
            />
            <QuoteCard
              quote="We believe its important to be open and honest on the actual condition of the road network, so we can be realistic when planning and programming repairs and letting residents know when they can expect to see improvements."
              source="Derby City Council Local Highway Maintenance Transparency Report (2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What this admission means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Derby proactively abandoned SCANNER in 2018 — before many authorities — because it considered
                AEI a truer reflection of maintenance need. The council accepts AEI produces a worse-looking
                condition picture than the national laser benchmark.
              </p>
              <p className="text-surface-600 text-sm">
                That is documented knowledge that the old methodology understated deterioration. It aligns
                with PAS 2161 standards arriving in 2026/27, which Derby references in its report.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions worth asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• If AEI shows worse condition, did inspection frequency increase to match?</li>
                <li>• Was your road&apos;s AEI section rated amber or red before your incident?</li>
                <li>• Did planned maintenance on the 2025/26 programme include your street?</li>
                <li>• Can the council cite AEI honesty while disputing a defect visible in your photos?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Planned maintenance activity */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Planned work vs reactive pressure
          </h2>
          <p className="text-surface-600 mb-8">
            Three years of maintenance activity from Derby&apos;s transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Activity</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">2022/23</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">2023/24</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">2024/25</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 text-brand-900">Carriageway resurfaced (km)</td>
                  <td className="px-4 py-3 text-center">9.6</td>
                  <td className="px-4 py-3 text-center">6.7</td>
                  <td className="px-4 py-3 text-center">8.6</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 text-brand-900">Carriageway preserved (km)</td>
                  <td className="px-4 py-3 text-center">15.4</td>
                  <td className="px-4 py-3 text-center">11.2</td>
                  <td className="px-4 py-3 text-center">15.1</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 text-brand-900">Footway resurfaced (km)</td>
                  <td className="px-4 py-3 text-center">8.6</td>
                  <td className="px-4 py-3 text-center">4.5</td>
                  <td className="px-4 py-3 text-center">6.5</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-brand-900">Footway preserved (km)</td>
                  <td className="px-4 py-3 text-center">13.1</td>
                  <td className="px-4 py-3 text-center">18.3</td>
                  <td className="px-4 py-3 text-center">15.4</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">2025/26 programme snapshot</h3>
            <div className="grid sm:grid-cols-2 gap-6 mb-6 text-sm">
              <ul className="text-brand-300 space-y-2">
                <li>• 9.5km carriageway resurfacing planned</li>
                <li>• 91,376m² carriageway preservation (surface dressing / micro surfacing)</li>
                <li>• 12,500m² patching programme</li>
                <li>• 4,193 potholes estimated (continuing upward trend)</li>
              </ul>
              <ul className="text-brand-300 space-y-2">
                <li>• London Road, Grampian Way, Max Road resurfacing</li>
                <li>• Moorway Lane in-situ recycling (81% material reused on Lime Lane in 2024)</li>
                <li>• A6 Duffield Road and Uttoxeter New Road surface treatments</li>
                <li>• £1.4m drainage improvements; £2.1m structures work</li>
              </ul>
            </div>
            <p className="text-brand-200 text-sm">
              Named schemes show where capital is flowing. If your incident was on an unlisted residential
              street, the gap between programme publicity and your road&apos;s treatment is itself evidence.
            </p>
          </div>
        </div>
      </section>

      {/* What AMBER means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against an AMBER city council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Derby runs a structured asset-management programme — here is how that shapes your claim
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN best practice — ISO-aligned asset management policy and strategy
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 80% preventative spend share in 2024/25, rising from 60% in 2020/21
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 100% annual AEI survey of A and B/C roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-road RED share improved from 10% to 7% in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented innovation — warm-mix asphalt, in-situ recycling, EMCCA regional planning
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-prepared Section 58 defence citing asset-management processes and inspection regimes.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — A-road green share halved from 79% to 30% since 2022
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 67% of B/C roads in RED or amber; 17% RED sustained since 2020
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 611km of U-roads with only 25–50% condition-surveyed annually
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 3,844 pothole repairs in 2024/25 — defects forming faster than programmes prevent
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits AEI shows worse condition than SCANNER — cannot have it both ways
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN best practice and an 80% preventative spend ratio, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, Derby report-it) — proof of actual notice</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the 25–50% survey gap is your strongest structural argument</li>
              <li>• A-road or B/C route — cite the AEI tables showing amber/red share on that network tier</li>
              <li>• Confirm you are claiming against Derby City Council, not Derbyshire County Council</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Derby&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Derby first
          </h2>
          <p className="text-surface-600 mb-6">
            Derby&apos;s transparency report covers reactive maintenance undertaken in line with Section 41
            and Section 58 statutory undertakings. Reporting the defect through the council creates a dated
            record — useful evidence if the pothole was reported before your incident, or if the council failed
            to repair it within a reasonable time.
          </p>
          <a
            href="https://www.derby.gov.uk/report-it/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to Derby City Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Use the council&apos;s online report-it form or myAccount service. Keep your reference number and any confirmation emails.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Derby?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A data-driven council demands an evidence-led claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road survey-gap argument</li>
                <li>• No A-road condition-collapse data</li>
                <li>• Risk claiming against Derbyshire by mistake</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ A-road green collapse 79% → 30% documented</li>
                <li>✅ 611km U-road partial-survey gap argued</li>
                <li>✅ 3,844 pothole repairs in 2024/25 cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Derby City Council</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=derby"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Derby claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Is Derby City Council the same as Derbyshire County Council for pothole claims?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Derby is a unitary city council maintaining 765.45km of carriageway within the city boundary.
                Derbyshire County Council maintains roads outside the city. If your incident was on a road inside
                Derby — for example London Road, Uttoxeter New Road or a residential street in Alvaston or
                Mickleover — your claim is against Derby City Council, not the county.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Derby earns a GREEN best-practice scorecard — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Best practice reflects asset-management processes, not whether the specific pothole that
                damaged your vehicle was reasonably inspected and repaired. Derby&apos;s own AEI data shows
                A-road &ldquo;green&rdquo; share fell from 79% in 2022 to 30% in 2024, B and C roads remain at
                17% RED, and the council filled an estimated 3,844 potholes in 2024/25. Section 58 turns on
                the defect, not the scorecard colour.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential street in Derby?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 610.94km — roughly 80% of Derby City Council&apos;s 765.45km network.
                The council surveys only 25% to 50% of U-roads each year under DfT reporting standards. At the
                last published survey, 7% of U-roads were in RED condition (down from a steady 10% in 2021–2023).
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Derby says rising pothole repairs mean roads are improving — does that weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Not necessarily — but it is an argument the council may deploy. Derby attributes higher repair
                counts partly to £1.2 million of DfT Pothole Fund patching and fixing multiple defects in one
                visit. That does not prove the specific defect that damaged your vehicle was caught in time.
                Prior reports, photos showing defect age, and the council&apos;s own condition tables remain
                the decisive evidence.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Derby&apos;s switch to AEI surveys help or hurt my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It cuts both ways. Derby moved to Annual Engineering Inspection in 2018 and states openly that
                AEI &ldquo;may show more of the road network in poor condition than SCANNER&rdquo; because it
                assesses entire maintenance sections. If your incident post-dates AEI reporting, the council
                chose a methodology that shows 61% of A-roads in amber condition — and cannot revert to
                SCANNER to downplay it.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Derby&apos;s capital spend now matches its DfT allocation — what does AMBER spend mean?
              </h3>
              <p className="text-surface-600 text-sm">
                From 2022/23 onwards Derby&apos;s capital spend has matched its DfT allocation year-on-year
                (unlike the £9 million cabinet boost split across 2020/21 and 2021/22). The AMBER spend
                scorecard reflects that recent alignment. For your claim, condition data matters more: 67% of
                B and C roads are in RED or amber categories, and reactive pothole filling still runs at
                roughly 3,800 repairs a year.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.derby.gov.uk/transport-and-streets/managing-highway-assets/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Derby City Council Local Highway Maintenance Transparency Report (November 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
